import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, cn, Dialog, Field, Input, Segmented, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StorageLocationClient,
  StorageLocationStockClient,
  type StorageLocationDTO,
  type StorageLocationStockDTO,
  type UnshelvedProduct,
} from "../../api/locations";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import { NexpimView } from "../../views";
import { FINISHED_GOODS_WAREHOUSE } from "./assembly";

const KEY = ["shelves"] as const;

/**
 * What is on each shelf of the finished-goods warehouse. A lot put away places
 * its units on its shelf; what is sold comes off the oldest placements at each
 * KPI recompute. Here people see the shelves by zone, move or correct what is
 * on them, and place what the warehouse holds beyond them.
 */
export function ShelvesPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.TERREBONNE_PACKING);
  const editable = canWrite;
  const stock = useMemo(() => new StorageLocationStockClient(http), [http]);
  const [view, setView] = useState<"shelved" | "unshelved">("shelved");
  const [shelf, setShelf] = useState<number | null>(null);
  const [placing, setPlacing] = useState<UnshelvedProduct | null>(null);

  const locations = useQuery({
    queryKey: [...KEY, "locations"],
    queryFn: () => new StorageLocationClient(http).forWarehouse(FINISHED_GOODS_WAREHOUSE),
    enabled: allowed,
  });
  const placements = useQuery({
    queryKey: [...KEY, "stock"],
    queryFn: () => stock.forWarehouse(FINISHED_GOODS_WAREHOUSE),
    enabled: allowed,
  });
  const shelves = locations.data ?? [];
  const warehouseId = shelves[0]?.warehouse_id;
  const unshelved = useQuery({
    queryKey: [...KEY, "unshelved", warehouseId],
    queryFn: () => stock.unshelved(warehouseId),
    enabled: allowed && warehouseId != null,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
  const shelfOptions = shelves.map((location) => ({ value: String(location.id), label: shelfLabel(location) }));
  const rows = (placements.data ?? []).filter((row) => shelf == null || row.storage_location_id === shelf);

  // Shelves by zone, in shelf order; a shelf without a zone goes last.
  const zones = useMemo(() => {
    const byZone = new Map<string, StorageLocationDTO[]>();
    for (const location of shelves) {
      const zone = location.zone ?? "";
      byZone.set(zone, [...(byZone.get(zone) ?? []), location]);
    }
    return [...byZone.entries()].sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  }, [shelves]);

  const cols = useMemo(() => {
    const c = defineColumns<StorageLocationStockDTO>();
    return [
      c.text("storage_location", (row) => row.storage_location_code ?? "", { header: t("shelves.column.shelf", "Shelf"), priority: 1 }),
      c.text("sku", (row) => row.sku ?? "", { header: t("shelves.column.sku", "SKU"), filter: "text", priority: 1 }),
      c.text("product_name", (row) => row.product_name ?? "", { headerKey: "column.name", priority: 3 }),
      c.custom(
        "quantity",
        (row) => Number(row.quantity),
        (row) => <span className="tabular-nums">{formatNumber(row.quantity)}</span>,
        { header: t("shelves.column.quantity", "Units"), priority: 1, align: "right" },
      ),
      c.text("placed_date", (row) => row.placed_date ?? "", { header: t("shelves.column.placed", "Placed"), priority: 2 }),
      c.text("lot", (row) => (row.production_order_number == null ? t("shelves.by-hand", "By hand") : String(row.production_order_number)), {
        header: t("shelves.column.lot", "From lot"),
        priority: 3,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 4 }),
    ];
  }, [t]);

  const unshelvedCols = useMemo(() => {
    const c = defineColumns<UnshelvedProduct>();
    const number = (key: "inventory" | "shelved" | "unshelved", header: string, priority: number) =>
      c.custom(
        key,
        (row) => Number(row[key]),
        (row) => <span className="tabular-nums">{formatNumber(row[key])}</span>,
        { header, priority, align: "right" as const },
      );
    return [
      c.text("sku", (row) => row.sku, { header: t("shelves.column.sku", "SKU"), filter: "text", priority: 1 }),
      c.text("name", (row) => row.name ?? "", { headerKey: "column.name", priority: 3 }),
      number("inventory", t("shelves.column.inventory", "In inventory"), 2),
      number("shelved", t("shelves.column.shelved", "On shelves"), 2),
      number("unshelved", t("shelves.column.unshelved", "Not on a shelf"), 1),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("shelves.title", "Shelves")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">{t("error.access-denied.detail", "Your account does not have permission to view this page.")}</p>
        </div>
      </>
    );
  }

  const unshelvedRows = unshelved.data ?? [];

  return (
    <>
      <PageHeader
        title={t("shelves.title", "Shelves")}
        subtitle={t(
          "shelves.description",
          "What is on each shelf. A lot put away is placed on its shelf; what is sold comes off the oldest placements when the inventory is recomputed.",
        )}
      />
      <div className="space-y-4 border-b border-line px-4 py-4 lg:px-6">
        {zones.map(([zone, members]) => (
          <section key={zone || "none"}>
            <h3 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-ink-muted">
              {zone || t("shelves.no-zone", "No zone")}
            </h3>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2">
              {members.map((location) => {
                const units = Number(location.shelved_units ?? 0);
                const selected = shelf === location.id;
                return (
                  <button
                    key={location.id}
                    type="button"
                    onClick={() => {
                      setShelf(selected ? null : location.id);
                      setView("shelved");
                    }}
                    className={cn(
                      "rounded-md border px-3 py-2 text-left transition-colors",
                      selected ? "border-accent bg-accent-soft" : "border-line bg-surface hover:bg-surface-hover",
                    )}
                  >
                    <span className="block text-[13px] font-medium">{shelfLabel(location)}</span>
                    <span className="block text-[11px] tabular-nums text-ink-muted">
                      {units === 0
                        ? t("shelves.empty", "Empty")
                        : t("shelves.card.units", "{{units}} units · {{products}} SKUs", {
                            units: formatNumber(units),
                            products: location.product_count ?? 0,
                          })}
                    </span>
                    {location.capacity_pallets != null && (
                      <span className="block text-[11px] text-ink-faint">
                        {t("shelves.card.capacity", "{{n}} pallets", { n: location.capacity_pallets })}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
        {!locations.isLoading && shelves.length === 0 && (
          <p className="text-[13px] text-ink-muted">{t("shelves.none", "No shelves yet: add them in Shop floor setup, Storage locations.")}</p>
        )}
      </div>
      {view === "shelved" ? (
        <DataTable<StorageLocationStockDTO>
          data={rows}
          columns={cols}
          getRowId={(row) => String(row.id)}
          loading={placements.isLoading}
          emptyMessage={shelf == null ? t("shelves.nothing", "Nothing on the shelves yet.") : t("shelves.shelf-empty", "Nothing on this shelf.")}
          toolbarExtra={
            <span className="flex flex-wrap items-center gap-2">
              <ViewToggle view={view} setView={setView} unshelved={unshelvedRows.length} />
              {shelf != null && (
                <Button variant="ghost" size="sm" onClick={() => setShelf(null)}>
                  {t("shelves.all", "All shelves")}
                </Button>
              )}
            </span>
          }
          editor={
            editable
              ? {
                  fields: {
                    storage_location: {
                      kind: "select",
                      get: (row) => String(row.storage_location_id),
                      set: (row, value) => ({ ...row, storage_location_id: Number(value) }),
                      options: () => shelfOptions,
                      required: true,
                    },
                    quantity: {
                      kind: "number",
                      get: (row) => (Number.isFinite(row.quantity) ? row.quantity : ""),
                      set: (row, value) => ({ ...row, quantity: value.trim() === "" ? Number.NaN : Number(value) }),
                    },
                    placed_date: {
                      kind: "text",
                      get: (row) => row.placed_date ?? "",
                      set: (row, value) => ({ ...row, placed_date: value.trim() === "" ? null : value.trim() }),
                    },
                    note: {
                      kind: "text",
                      get: (row) => row.note ?? "",
                      set: (row, value) => ({ ...row, note: value.trim() === "" ? null : value }),
                    },
                  },
                  validate: (row) => {
                    const errors: Record<string, string> = {};
                    if (!(row.quantity > 0)) errors.quantity = t("shelves.error.quantity", "More than zero; delete the row when the shelf is empty of it.");
                    if (row.placed_date != null && !/^\d{4}-\d{2}-\d{2}$/.test(row.placed_date)) {
                      errors.placed_date = t("shelves.error.date", "A date as 2026-10-22.");
                    }
                    return Object.keys(errors).length === 0 ? null : errors;
                  },
                  onSave: async (row) => {
                    try {
                      await stock.update(row);
                      await refresh();
                    } catch (failure) {
                      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                      throw failure;
                    }
                  },
                  onDelete: async (row) => {
                    await stock.remove(row.id);
                    await refresh();
                  },
                  deleteConfirm: (row) => ({
                    title: t("shelves.delete", "Take {{sku}} off {{shelf}}?", { sku: row.sku ?? "", shelf: row.storage_location_code ?? "" }),
                  }),
                }
              : undefined
          }
        />
      ) : (
        <DataTable<UnshelvedProduct>
          data={unshelvedRows}
          columns={unshelvedCols}
          getRowId={(row) => `${row.warehouse_id}-${row.product_id}`}
          loading={unshelved.isLoading}
          emptyMessage={t("shelves.all-shelved", "Everything in the warehouse is on a shelf.")}
          toolbarExtra={<ViewToggle view={view} setView={setView} unshelved={unshelvedRows.length} />}
          rowActions={
            editable && shelves.length > 0
              ? (row) => (
                  <Button variant="secondary" size="sm" onClick={() => setPlacing(row)}>
                    {t("shelves.place", "Place")}
                  </Button>
                )
              : undefined
          }
        />
      )}
      {placing && <PlaceDialog product={placing} shelves={shelves} onClose={() => setPlacing(null)} onPlaced={refresh} />}
    </>
  );
}

function shelfLabel(location: StorageLocationDTO): string {
  return location.name ? `${location.code} · ${location.name}` : location.code;
}

function ViewToggle({
  view,
  setView,
  unshelved,
}: {
  view: "shelved" | "unshelved";
  setView: (view: "shelved" | "unshelved") => void;
  unshelved: number;
}) {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-1.5">
      <Segmented
        value={view}
        onChange={setView}
        options={[
          { value: "shelved", label: t("shelves.view.shelved", "On the shelves") },
          { value: "unshelved", label: t("shelves.view.unshelved", "Not on a shelf") },
        ]}
      />
      {unshelved > 0 && (
        <Badge variant="warning" size="sm">
          {formatNumber(unshelved)}
        </Badge>
      )}
    </span>
  );
}

/** Places units of a product the warehouse holds beyond its shelves. */
function PlaceDialog({
  product,
  shelves,
  onClose,
  onPlaced,
}: {
  product: UnshelvedProduct;
  shelves: StorageLocationDTO[];
  onClose: () => void;
  onPlaced: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const stock = useMemo(() => new StorageLocationStockClient(http), [http]);
  const [shelfId, setShelfId] = useState("");
  const [quantity, setQuantity] = useState(String(Number(product.unshelved)));
  const [note, setNote] = useState("");
  const amount = Number(quantity);
  const valid = shelfId !== "" && Number.isFinite(amount) && amount > 0;

  const place = useMutation({
    mutationFn: () =>
      stock.create({
        id: 0,
        storage_location_id: Number(shelfId),
        product_id: product.product_id,
        quantity: amount,
        placed_date: null,
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: async () => {
      await onPlaced();
      onClose();
      toast.success(t("shelves.placed", "Placed"));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("shelves.place.title", "Place {{sku}}", { sku: product.sku })}
      description={t("shelves.place.description", "{{n}} in the warehouse are not on a shelf.", { n: formatNumber(product.unshelved) })}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button variant="primary" disabled={!valid} loading={place.isPending} onClick={() => place.mutate()}>
            {t("shelves.place", "Place")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t("shelves.column.shelf", "Shelf")} htmlFor="place-shelf">
          <Select
            id="place-shelf"
            value={shelfId}
            placeholder={t("packing.put-away.choose", "Choose a location")}
            options={shelves.map((location) => ({ value: String(location.id), label: shelfLabel(location) }))}
            onChange={(event) => setShelfId(event.target.value)}
          />
        </Field>
        <Field label={t("shelves.column.quantity", "Units")} htmlFor="place-quantity">
          <Input id="place-quantity" type="number" min="1" step="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
        </Field>
        <Field label={t("column.note", "Note")} htmlFor="place-note">
          <Input id="place-note" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
